import React, { useCallback, useRef, useState } from "react";
import { useForm } from "react-final-form";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuProps } from "@mui/material";

import { DEFAULT_SCORING_INPUT_VALUE, ScoringInputConfig } from "fond/cityPlanner/assessConfigurationHelper";
import { ScoreFactor } from "fond/types";

import { ScoringItemsMenuItem } from "./ScoringItemsMenuItem";
import ScoringItemsSearchMenu from "./ScoringItemsSearchMenu";

type ScoringItemsMenuProps = Omit<MenuProps, "open" | "anchorEl" | "onClose"> & {
  id: string;
  factor: ScoreFactor;
  scoringInputConfigs: ScoringInputConfig[];
  candidateScoringInputConfigs: ScoringInputConfig[];
};

export const ScoringItemsMenu: React.FC<ScoringItemsMenuProps> = ({
  id,
  factor,
  scoringInputConfigs,
  candidateScoringInputConfigs,
  ...props
}: ScoringItemsMenuProps) => {
  const { getFieldState } = useForm(); // use the form state rather than field value because it works more reliably in tests
  const [state, setState] = useState<"hidden" | "main" | "search">("hidden");
  const anchorEl = useRef<null | HTMLButtonElement>(null);

  const showSearch = useCallback(() => setState("search"), [setState]);
  const showMain = useCallback(() => setState("main"), [setState]);
  const hide = useCallback(() => setState("hidden"), [setState]);
  const toggleOpen = useCallback(() => {
    setState((currentState) => {
      if (currentState === "hidden") return "main";
      return "hidden";
    });
  }, [setState]);

  const candidateItems = candidateScoringInputConfigs.filter((input) => (factor ? input.factor === factor : true));
  const items = scoringInputConfigs.filter((input) => (factor ? input.factor === factor : true));

  /**
   * Selecting an item is analogous to wanting a value for this field, so we set it to the default value.
   */
  const onSelect = useCallback(
    (itemId: string) => {
      const fieldState = getFieldState(itemId);
      if (fieldState) {
        if (fieldState.value == null) {
          fieldState.change(DEFAULT_SCORING_INPUT_VALUE);
        } else {
          fieldState.change(null);
        }
      }
    },
    [getFieldState]
  );

  return (
    <>
      <Button ref={anchorEl} color="primary" onClick={toggleOpen} fullWidth aria-haspopup="true" aria-controls={id} aria-expanded="false">
        <AddIcon fontSize="small" /> Add input
      </Button>
      <Menu
        id={id}
        {...props}
        open={state !== "hidden"}
        onClose={hide}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {state === "main" && (
          <>
            {items.map((item) => (
              <ScoringItemsMenuItem key={item.id} item={item} onSelect={onSelect} />
            ))}
            {candidateItems.length > 0 && (
              <MenuItem onClick={showSearch} dense>
                <ListItemIcon>
                  <SearchIcon />
                </ListItemIcon>
                <ListItemText primary="Search for more..." secondary={`${candidateItems.length} additional options`} />
              </MenuItem>
            )}
          </>
        )}
        {state === "search" && <ScoringItemsSearchMenu candidateItems={candidateItems} onSelect={onSelect} onClose={hide} onBack={showMain} />}
      </Menu>
    </>
  );
};
